// ============================================================================
// ConsolidationModal — Personal Finance V2
// Modal de consolidação em lote: permite liquidar múltiplos itens de uma vez.
// Padrão ouro: QuickBooks "Receive Payments" + apps bancários
// Fluxo: selecionar itens → definir data de pagamento → liquidar tudo de uma vez
// ============================================================================

import React, { useState, useMemo } from "react";

const formatCurrency = (val) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(val);

export function ConsolidationModal({ postings, onClose, onConsolidate }) {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [settlementDate, setSettlementDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [consolidating, setConsolidating] = useState(false);
  // Filtro por Responsável (pedido do titular, 18/09/2026): pagar/reembolsar
  // tudo de uma pessoa só (ex: Eliane) exigia caçar item por item numa lista
  // única sem separação — "" = todos os responsáveis.
  const [vinculoFilter, setVinculoFilter] = useState("");

  // Filtra postings não pagos e não cancelados (PENDING, OVERDUE, FORECAST)
  // Exclui RECEITAS — não se "liquida" receita
  const consolidatable = useMemo(() => {
    return (postings || []).filter(
      (p) =>
        p.status !== "PAID" &&
        p.status !== "CANCELLED" &&
        p.group !== "RECEITAS",
    );
  }, [postings]);

  const vinculoOptions = useMemo(() => {
    return [...new Set(consolidatable.map((p) => p.vinculo).filter(Boolean))].sort();
  }, [consolidatable]);

  const visibleItems = useMemo(() => {
    if (!vinculoFilter) return consolidatable;
    return consolidatable.filter((p) => p.vinculo === vinculoFilter);
  }, [consolidatable, vinculoFilter]);

  const totalSelected = useMemo(() => {
    return consolidatable
      .filter((p) => selectedIds.has(p.id))
      .reduce((sum, p) => sum + Number(p.value), 0);
  }, [consolidatable, selectedIds]);

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  // Seleciona/desmarca apenas os itens visíveis no filtro atual — seleções
  // feitas com outro filtro (ex: já marcou uns da Eliane, trocou pra "Todos")
  // continuam marcadas, permitindo consolidar responsáveis diferentes juntos
  // se for o caso.
  const selectAll = () => {
    const allVisibleSelected =
      visibleItems.length > 0 &&
      visibleItems.every((p) => selectedIds.has(p.id));
    const next = new Set(selectedIds);
    if (allVisibleSelected) {
      visibleItems.forEach((p) => next.delete(p.id));
    } else {
      visibleItems.forEach((p) => next.add(p.id));
    }
    setSelectedIds(next);
  };

  const handleConsolidate = async () => {
    if (selectedIds.size === 0) return;
    setConsolidating(true);
    try {
      const items = consolidatable.filter((p) => selectedIds.has(p.id));
      await onConsolidate(items, settlementDate);
      setSelectedIds(new Set());
      onClose();
    } catch (e) {
      alert("Erro ao consolidar: " + e.message);
    } finally {
      setConsolidating(false);
    }
  };

  const statusLabel = (status) => {
    if (status === "OVERDUE")
      return { text: "Atrasado", color: "text-red-600 bg-red-50" };
    if (status === "PENDING")
      return { text: "Aberto", color: "text-amber-600 bg-amber-50" };
    if (status === "FORECAST")
      return { text: "Previsto", color: "text-purple-600 bg-purple-50" };
    return { text: status, color: "text-gray-600 bg-gray-50" };
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm">
      <div className="bg-white w-full max-w-lg overflow-hidden border border-[#d1d5db] shadow-2xl">
        {/* Title bar */}
        <div className="flex items-center justify-between px-4 py-2 bg-[#20243B]">
          <span className="text-white text-[11px] font-bold">
            <i className="fas fa-check-double mr-1.5"></i>
            Consolidar Lançamentos
          </span>
          <button onClick={onClose} className="text-white/60 hover:text-white">
            <i className="fas fa-times text-[11px]"></i>
          </button>
        </div>

        <div className="p-4 space-y-3 max-h-[60vh] overflow-y-auto">
          {/* Data de pagamento global */}
          <div className="flex items-center justify-between bg-amber-50 border border-amber-200 px-3 py-2">
            <span className="text-[10px] font-semibold text-amber-800">
              <i className="fas fa-calendar-check mr-1"></i>
              Data de pagamento
            </span>
            <input
              type="date"
              value={settlementDate}
              onChange={(e) => setSettlementDate(e.target.value)}
              max={new Date().toISOString().split("T")[0]}
              className="text-[10px] border border-amber-300 bg-white px-2 py-1 font-mono w-36 text-center"
            />
          </div>

          {/* Filtro por Responsável — separa quem vai ser pago/reembolsado */}
          {vinculoOptions.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 pb-1">
              <span className="text-[9px] font-bold text-gray-400 uppercase">
                Responsável:
              </span>
              <button
                onClick={() => setVinculoFilter("")}
                className={`px-2 py-0.5 rounded-full text-[9px] font-bold border ${
                  vinculoFilter === ""
                    ? "bg-[#20243B] text-white border-[#20243B]"
                    : "bg-white text-gray-500 border-gray-300 hover:bg-gray-50"
                }`}
              >
                Todos ({consolidatable.length})
              </button>
              {vinculoOptions.map((v) => {
                const count = consolidatable.filter((p) => p.vinculo === v).length;
                const nome = v.charAt(0).toUpperCase() + v.slice(1);
                return (
                  <button
                    key={v}
                    onClick={() => setVinculoFilter(v)}
                    className={`px-2 py-0.5 rounded-full text-[9px] font-bold border ${
                      vinculoFilter === v
                        ? "bg-pink-600 text-white border-pink-600"
                        : "bg-pink-50 text-pink-700 border-pink-200 hover:bg-pink-100"
                    }`}
                  >
                    {nome} ({count})
                  </button>
                );
              })}
            </div>
          )}

          {/* Select all (respeita o filtro por Responsável acima) */}
          <div className="flex items-center justify-between border-b border-gray-200 pb-2">
            <label className="flex items-center gap-2 text-[10px] font-semibold text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={
                  visibleItems.length > 0 &&
                  visibleItems.every((p) => selectedIds.has(p.id))
                }
                onChange={selectAll}
                className="w-3.5 h-3.5 accent-[#20243B]"
              />
              Selecionar todos ({visibleItems.length})
            </label>
            <span className="text-[11px] font-bold text-emerald-700 tabular-nums">
              {formatCurrency(totalSelected)}
            </span>
          </div>

          {/* Lista de itens */}
          {visibleItems.length === 0 && (
            <div className="text-center py-8 text-gray-400 text-[11px]">
              <i className="fas fa-inbox text-2xl mb-2 block"></i>
              {vinculoFilter
                ? "Nenhum lançamento pendente para este responsável."
                : "Nenhum lançamento pendente para liquidar."}
            </div>
          )}

          {visibleItems.map((p) => {
            const st = statusLabel(p.status);
            return (
              <div
                key={p.id}
                className={`flex items-center gap-3 px-2 py-1.5 rounded cursor-pointer transition-colors ${
                  selectedIds.has(p.id)
                    ? "bg-emerald-50 border border-emerald-200"
                    : "hover:bg-gray-50 border border-transparent"
                }`}
                onClick={() => toggleSelect(p.id)}
              >
                <input
                  type="checkbox"
                  checked={selectedIds.has(p.id)}
                  onChange={() => toggleSelect(p.id)}
                  className="w-3.5 h-3.5 accent-[#20243B]"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-medium text-gray-800 truncate">
                      {p.description}
                    </span>
                    <span
                      className={`text-[8px] font-bold px-1 py-0.5 rounded ${st.color}`}
                    >
                      {st.text}
                    </span>
                  </div>
                  <div className="text-[9px] text-gray-400 mt-0.5">
                    {p.category} &middot;{" "}
                    {p.date
                      ? new Date(p.date + "T12:00:00").toLocaleDateString(
                          "pt-BR",
                        )
                      : "—"}
                    {p.parcela ? ` &middot; ${p.parcela}` : ""}
                    {p.account ? ` &middot; ${p.account}` : ""}
                  </div>
                </div>
                <span className="text-[11px] font-bold text-gray-700 tabular-nums">
                  {formatCurrency(p.value)}
                </span>
              </div>
            );
          })}
        </div>

        {/* Action bar */}
        <div className="flex items-center justify-between px-4 py-2 bg-[#f3f4f6] border-t border-[#d1d5db]">
          <span className="text-[10px] text-gray-500">
            {selectedIds.size} selecionado(s) — {formatCurrency(totalSelected)}
          </span>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-[10px] font-semibold text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleConsolidate}
              disabled={selectedIds.size === 0 || consolidating}
              className={`px-3 py-1.5 text-[10px] font-bold text-white flex items-center gap-1 ${
                selectedIds.size === 0 || consolidating
                  ? "bg-gray-300 cursor-not-allowed"
                  : "bg-[#059669] hover:bg-[#047857]"
              }`}
            >
              {consolidating ? (
                <i className="fas fa-spinner fa-spin"></i>
              ) : (
                <i className="fas fa-check"></i>
              )}
              {consolidating
                ? "Liquidando..."
                : `Liquidar ${selectedIds.size} item(ns)`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
